import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  ReceiptLong as ReceiptLongIcon,
  ShoppingCart as ShoppingCartIcon,
  CallSplit as CallSplitIcon,
  RequestQuote as RequestQuoteIcon,
  Storage as StorageIcon,
  AddShoppingCart as AddShoppingCartIcon,
  SwapVert as SwapVertIcon,
  TrendingUp as TrendingUpIcon,
  Replay as ReplayIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PartnerLayout from '../../components/partner/PartnerLayout';
import PartnerHelpPanel from '../../components/partner/PartnerHelpPanel';
import PurchaseCreditsDialog from '../../components/partner/PurchaseCreditsDialog';
import LimitRequestDialog from '../../components/partner/LimitRequestDialog';
import LimitRequestStatusChip from '../../components/LimitRequestStatusChip';
import partnerApiService from '../../services/partnerApi';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import {
  PartnerLimitRequest,
  PartnerStatement,
  PartnerStatementClientRow,
  PartnerWholesaleEntry
} from '../../types/partner';
import { formatCredits, firstDefined } from '../../utils/credits';

// Facturación del PORTAL PARTNER. Tres bloques, y los dos últimos existen
// porque el statement solo no bastaba:
//   1. STATEMENT mensual (GET /partner/statement): agregados del mes y total a
//      facturar. Contesta «cuánto», no «cuál».
//   2. EXTRACTO de la bolsa (GET /partner/transactions): movimiento a
//      movimiento. Es lo que permite contestar a «¿por qué me bajó el saldo el
//      martes?» — antes había que preguntárselo al marketplace.
//   3. COMPRAS y LÍMITE MENSUAL: el partner COMPRA créditos a granel él mismo
//      desde aquí, libremente, mientras lo comprado este mes + la compra quepan
//      en su LÍMITE DE COMPRA MENSUAL. Lo único que pide al marketplace es SUBIR
//      ese límite. Antes su bolsa solo subía si un admin la recargaba a mano.
// Sin pasarela: la factura llega mensualmente.
//
// UNIDADES, que es donde se confundía todo: los CRÉDITOS son la unidad de la
// bolsa y el IMPORTE es dinero (USD). Ninguna cifra de esta página lleva la
// etiqueta equivocada — donde pone «importe» hay USD, y donde hay créditos pone
// «créditos».

// Etiqueta legible de cada tipo de asiento del ledger mayorista. `allocation`
// es negativo (sale de tu bolsa hacia un cliente) y `storage` también: la tabla
// ya lo pinta por el signo, pero el nombre tiene que decirlo igual.
const KIND_FILTERS = ['', 'purchase', 'allocation', 'adjustment', 'storage'] as const;

const PartnerBillingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  // El saldo mayorista vive en /partner/me (lo pinta el navbar): se reutiliza
  // para el diálogo de solicitud y se refresca cuando algo lo mueve.
  const { reseller, refreshMe } = usePartnerAuth();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [statement, setStatement] = useState<PartnerStatement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Extracto de la bolsa + filtro por tipo de movimiento.
  const [entries, setEntries] = useState<PartnerWholesaleEntry[]>([]);
  const [entriesKind, setEntriesKind] = useState<string>('');
  const [entriesLoading, setEntriesLoading] = useState(true);

  // Compra self-service y solicitudes de ampliación de techo.
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [requests, setRequests] = useState<PartnerLimitRequest[]>([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [cancelling, setCancelling] = useState<number | null>(null);

  const loadStatement = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await partnerApiService.getStatement(year, month);
      setStatement(data);
    } catch (err: any) {
      console.error('Failed to load partner statement:', err);
      // Mes sin datos / backend aún sin el endpoint → estado vacío, no error duro.
      setStatement(null);
      if (err?.detail && typeof err.detail === 'string') {
        setError(err.detail);
      }
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadStatement();
  }, [loadStatement]);

  // Extracto y solicitudes son INDEPENDIENTES del mes seleccionado (uno es el
  // histórico de la bolsa, otras son el estado de tus peticiones): se cargan
  // por separado para que cambiar de mes no las recargue sin motivo, y para que
  // un fallo del statement no se lleve por delante estos bloques.
  const loadEntries = useCallback(async () => {
    try {
      setEntriesLoading(true);
      setEntries(await partnerApiService.getTransactions(entriesKind || undefined));
    } catch (err) {
      console.error('Failed to load wholesale entries:', err);
      setEntries([]);
    } finally {
      setEntriesLoading(false);
    }
  }, [entriesKind]);

  const loadRequests = useCallback(async () => {
    try {
      setRequests(await partnerApiService.getLimitRequests());
    } catch (err) {
      console.error('Failed to load limit requests:', err);
      setRequests([]);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // Una compra mueve el extracto, el saldo del navbar y el statement del mes en
  // curso: se refresca todo, porque dejar cualquiera de los tres desfasado hace
  // dudar de la cifra que sí es correcta.
  const refreshAfterPurchase = () => {
    loadEntries();
    loadStatement();
    refreshMe();
  };

  // Una solicitud de techo NO mueve saldo: solo su propia lista (y el techo del
  // navbar cuando el admin la apruebe, que llegará en el siguiente /me).
  const refreshAfterRequest = () => {
    loadRequests();
    refreshMe();
  };

  const handleCancelRequest = async (request: PartnerLimitRequest) => {
    try {
      setCancelling(request.id);
      await partnerApiService.cancelLimitRequest(request.id);
      toast.success(t('partner.limitRequest.cancelled', 'Solicitud retirada'));
      loadRequests();
    } catch (err: any) {
      console.error('Failed to cancel limit request:', err);
      toast.error(err?.detail || t('partner.limitRequest.cancelError', 'No se pudo retirar la solicitud'));
    } finally {
      setCancelling(null);
    }
  };

  const entryKindLabel = (kind: string): string => {
    const labels: Record<string, string> = {
      purchase: t('partner.transactions.kind.purchase', 'Compra'),
      allocation: t('partner.transactions.kind.allocation', 'Asignación a cliente'),
      adjustment: t('partner.transactions.kind.adjustment', 'Ajuste'),
      storage: t('partner.transactions.kind.storage', 'Almacenamiento')
    };
    return labels[kind] ?? kind;
  };

  const formatMoment = (raw: string): string => {
    const utc = raw.endsWith('Z') ? raw : `${raw}Z`;
    const d = new Date(utc);
    return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
  };

  // LÍMITE DE COMPRA MENSUAL del partner. 0 = SIN límite (semántica del backend,
  // no «cero crédito»), y entonces no hay hueco que calcular ni ampliación que
  // pedir. El hueco se mide contra lo COMPRADO este ciclo, NO contra el saldo:
  // asignar a clientes baja el saldo pero no libera cupo de compra. Todo viene
  // de /partner/me: una sola fuente.
  const capCredits = Number(reseller?.max_wholesale_credits ?? 0);
  const hasCap = capCredits > 0;
  const purchasedThisCycle = Number(reseller?.wholesale_purchased_this_cycle ?? 0);
  const headroomCredits = hasCap ? Math.max(0, capCredits - purchasedThisCycle) : null;
  const cycleRenewsAt = reseller?.wholesale_cycle_renews_at ?? null;
  const renewsLabel = cycleRenewsAt
    ? new Date(cycleRenewsAt).toLocaleDateString(i18n.language || 'es')
    : null;

  // Las COMPRAS salen del extracto ya cargado (son sus asientos `purchase`), sin
  // una segunda llamada: la tabla de compras y el extracto miran exactamente los
  // mismos datos, así que no pueden discrepar. Si el filtro del extracto está en
  // otro tipo, se piden aparte — el bloque de compras no puede quedarse vacío
  // porque el usuario esté filtrando el de abajo.
  // OJO al nombre: `purchases` (más abajo) es el TOTAL DE CRÉDITOS comprados en
  // el mes del statement; esto son los ASIENTOS de compra, que es otra cosa.
  const [purchaseEntries, setPurchaseEntries] = useState<PartnerWholesaleEntry[]>([]);
  useEffect(() => {
    if (entriesKind === '') {
      setPurchaseEntries(entries.filter((e) => e.kind === 'purchase'));
      return;
    }
    if (entriesKind === 'purchase') {
      setPurchaseEntries(entries);
      return;
    }
    let cancelled = false;
    partnerApiService
      .getTransactions('purchase')
      .then((rows) => { if (!cancelled) setPurchaseEntries(rows); })
      .catch((err) => { console.error('Failed to load purchases:', err); });
    return () => { cancelled = true; };
  }, [entries, entriesKind]);

  // Años seleccionables: desde 2025 hasta el actual (el programa partner es reciente).
  const years: number[] = [];
  for (let y = now.getFullYear(); y >= 2025; y--) years.push(y);

  const monthName = (m: number) =>
    new Date(2026, m - 1, 1).toLocaleDateString(i18n.language || 'es', { month: 'long' });

  // El backend real (MonthlyStatementOut) envía clients/purchases_total/
  // allocated_total/amount_due_usd; los alias sin sufijo son del contrato
  // provisional con el que nació esta página — se mantienen como fallback.
  const rows: PartnerStatementClientRow[] = statement?.clients ?? statement?.by_client ?? [];
  const rowName = (row: PartnerStatementClientRow, idx: number) =>
    firstDefined(row.name, row.client_name, row.account_name) ||
    `#${firstDefined(row.client_account_id, row.account_id, row.client_id, row.id) ?? idx + 1}`;
  const rowAllocated = (row: PartnerStatementClientRow) => firstDefined(row.allocated, row.credits_allocated);
  const rowConsumed = (row: PartnerStatementClientRow) => firstDefined(row.consumed, row.consumption);
  // La columna de consumo solo se pinta si ALGUNA fila la trae: el contrato
  // actual del statement no la envía, y una columna entera a «—» solo añade
  // ruido a una tabla que se usa para cuadrar dinero.
  const hasConsumedColumn = rows.some((row) => rowConsumed(row) !== null && rowConsumed(row) !== undefined);

  const purchases = statement ? firstDefined(statement.purchases_total, statement.purchases) : null;
  const allocated = statement ? firstDefined(statement.allocated_total, statement.allocated) : null;
  const totalDue = statement ? firstDefined(statement.amount_due_usd, statement.total_due) : null;
  // Precio de lista vs TU precio: lista − descuento = a pagar; la diferencia
  // es el margen/ahorro del mes del partner.
  const grossAmount = statement ? firstDefined(statement.gross_amount_usd, statement.purchases_total) : null;
  const discountPct = statement ? firstDefined(statement.wholesale_discount_pct) : null;
  // PRECIO DE VENTA del crédito aplicado a esta factura (CREDIT_PRICE_USD del
  // backend, que viaja en el propio statement). NO es la tasa de coste del
  // consumo: aquí se factura, así que la leyenda tiene que decir a cuánto se
  // compra el crédito. Sin el dato (statement anterior a este contrato) no se
  // pinta leyenda — inventar un número en una factura es lo peor posible.
  const creditPrice = statement ? firstDefined(statement.credit_price_usd) : null;
  const creditPriceLabel =
    creditPrice === null || creditPrice === undefined || Number.isNaN(Number(creditPrice))
      ? null
      : t('partner.billing.creditPrice', '1 crédito = {{price}} USD', {
          price: Number(creditPrice).toFixed(2)
        });
  const savings =
    grossAmount !== null && totalDue !== null && !Number.isNaN(Number(grossAmount)) && !Number.isNaN(Number(totalDue))
      ? Number(grossAmount) - Number(totalDue)
      : null;

  // Cargo del ALMACENAMIENTO de la cartera en el mes: se paga con créditos de la
  // bolsa (no se suma al importe a facturar, que solo cobra las COMPRAS), y por
  // eso va como tarjeta de créditos junto a las asignaciones. Sin el dato
  // (backend anterior a la migración 039) la tarjeta no se pinta.
  const storageCredits = statement ? firstDefined(statement.storage_total) : null;
  const storageBillableGib = statement?.storage_billable_gib ?? null;
  const storageIncludedGib = statement?.storage_included_gib ?? null;
  // Devoluciones de plan no consumido acreditadas este mes: el remanente que
  // tus clientes no gastaron y vuelve a tu bolsa al cerrar su ciclo. Créditos
  // (no importe): tu coste real de sus planes es lo consumido, no el grant.
  // Sin el dato (backend anterior a este contrato) la tarjeta no se pinta.
  const planRefundCredits = statement ? firstDefined(statement.plan_refunds_total) : null;

  const summaryCards: {
    icon: React.ReactNode;
    label: string;
    value: number | string | null | undefined;
    hint: string;
  }[] = [
    {
      icon: <ShoppingCartIcon />,
      label: t('partner.billing.purchases', 'Compras de bolsa'),
      value: purchases,
      hint: t('partner.billing.purchasesHint', 'Créditos mayoristas comprados este mes')
    },
    {
      icon: <CallSplitIcon />,
      label: t('partner.billing.allocated', 'Asignado a clientes'),
      value: allocated,
      hint: t('partner.billing.allocatedHint', 'Créditos repartidos a tu cartera este mes')
    },
    ...(storageCredits === null || storageCredits === undefined
      ? []
      : [{
          icon: <StorageIcon />,
          label: t('partner.billing.storage', 'Almacenamiento de la cartera'),
          value: storageCredits,
          hint: storageBillableGib === null
            ? t('partner.billing.storageHint',
                'Créditos cobrados este mes por el disco de tus clientes')
            : t('partner.billing.storageHintGib',
                '{{gib}} GiB por encima de los {{included}} incluidos: se cobra por ciclo con créditos de tu bolsa',
                { gib: storageBillableGib, included: storageIncludedGib ?? 0 })
        }]),
    // Devoluciones de plan no consumido: solo si hubo alguna este mes (créditos
    // que VUELVEN a tu bolsa). Como el almacenamiento, es una línea de créditos,
    // no de importe: no se suma a «a pagar».
    ...(planRefundCredits === null || planRefundCredits === undefined
        || Number(planRefundCredits) === 0
      ? []
      : [{
          icon: <ReplayIcon />,
          label: t('partner.billing.planRefunds', 'Devoluciones de plan'),
          value: planRefundCredits,
          hint: t('partner.billing.planRefundsHint',
            'Créditos que vuelven a tu bolsa: el remanente que tus clientes no gastaron al cerrar su ciclo de plan')
        }])
  ];

  return (
    <PartnerLayout>
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <ReceiptLongIcon color="primary" fontSize="large" />
            <Typography variant="h4">{t('partner.billing.title', 'Facturación')}</Typography>
            {/* Ayuda de ESTA pantalla: qué es cada cifra y qué pasa al pulsar
                cada botón. El «saber más» lleva a la guía global. */}
            <PartnerHelpPanel screen="billing" variant="icon" />
          </Box>
          <Box display="flex" gap={1.5}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>{t('partner.billing.month', 'Mes')}</InputLabel>
              <Select
                value={month}
                label={t('partner.billing.month', 'Mes')}
                onChange={(e) => setMonth(Number(e.target.value))}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <MenuItem key={m} value={m}>{monthName(m)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>{t('partner.billing.year', 'Año')}</InputLabel>
              <Select
                value={year}
                label={t('partner.billing.year', 'Año')}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Aviso: modelo sin pasarela */}
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('partner.billing.noGateway', 'Se factura mensualmente, sin pasarela de pago: al cierre de mes recibirás la factura por el total indicado.')}
        </Alert>

        {error && <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Resumen del periodo */}
            <Grid container spacing={3} mb={3}>
              {summaryCards.map((card) => (
                <Grid item xs={12} sm={6} md={4} key={card.label}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent sx={{ py: 3 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1.5} color="primary.main">
                        {card.icon}
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          {card.label}
                        </Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {card.value === null || card.value === undefined ? '—' : formatCredits(card.value)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {card.hint}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12} sm={6} md={4}>
                {/* Total a facturar — la tarjeta protagonista */}
                <Card sx={{ height: '100%', bgcolor: 'primary.main', color: '#fff' }}>
                  <CardContent sx={{ py: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <RequestQuoteIcon />
                      <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.9 }}>
                        {t('partner.billing.totalDue', 'Total a facturar')}
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                      {totalDue === null ? '—' : `${formatCredits(totalDue)} USD`}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                      {t('partner.billing.totalDueHint', '{{month}} de {{year}} · factura mensual', {
                        month: monthName(month),
                        year
                      })}
                    </Typography>
                    {/* Precio de VENTA del crédito: el importe es créditos
                        comprados × precio, menos el descuento mayorista. */}
                    {creditPriceLabel && (
                      <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>
                        {creditPriceLabel}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Precio de lista vs TU precio: hace visible el margen del partner
                (lista − descuento mayorista = a pagar; la diferencia es tu
                ahorro del mes). Solo con datos del periodo. */}
            {grossAmount !== null && (
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ py: 2.5 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    columnGap={4}
                    rowGap={1.5}
                  >
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t('partner.billing.listPrice', 'Precio de lista')}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {formatCredits(grossAmount)} USD
                      </Typography>
                      {/* El precio de lista sale del precio de VENTA:
                          créditos comprados × CREDIT_PRICE_USD. */}
                      {creditPriceLabel && (
                        <Typography variant="caption" color="text.secondary">
                          {creditPriceLabel}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {discountPct === null
                          ? t('partner.billing.yourPrice', 'Tu precio')
                          : t('partner.billing.yourPriceWithPct', 'Tu precio (−{{pct}}%)', {
                              pct: Number(discountPct)
                            })}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="primary.main">
                        {totalDue === null ? '—' : `${formatCredits(totalDue)} USD`}
                      </Typography>
                    </Box>
                    {savings !== null && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('partner.billing.monthSavings', 'Tu ahorro este mes')}
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="success.main">
                          {formatCredits(savings)} USD
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', maxWidth: 380 }}>
                      {t(
                        'partner.billing.marginExplainer',
                        'Ese descuento es tu margen: compras a precio de lista menos tu descuento y decides el precio al que asignas créditos a tus clientes.'
                      )}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Desglose por cliente */}
            <Card>
              <CardContent>
                <Typography variant="h6" mb={2}>
                  {t('partner.billing.byClient', 'Desglose por cliente')}
                </Typography>
                {rows.length === 0 ? (
                  <Typography color="text.secondary" align="center" py={4}>
                    {t('partner.billing.noData', 'Sin datos para este periodo.')}
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('partner.billing.fields.client', 'Cliente')}</TableCell>
                          <TableCell align="right">{t('partner.billing.fields.allocated', 'Créditos asignados')}</TableCell>
                          {/* «Consumo» solo si el backend lo envía de verdad: una
                              columna permanentemente a «—» es ruido. La columna
                              «Importe» se retiró — repetía los créditos asignados
                              bajo un rótulo de dinero, que es justo la confusión
                              que este desglose tenía que evitar. */}
                          {hasConsumedColumn && (
                            <TableCell align="right">{t('partner.billing.fields.consumed', 'Consumo (créditos)')}</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {rowName(row, idx)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {rowAllocated(row) === null ? '—' : formatCredits(rowAllocated(row))}
                            </TableCell>
                            {hasConsumedColumn && (
                              <TableCell align="right">
                                {rowConsumed(row) === null ? '—' : formatCredits(rowConsumed(row))}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </>
        )}

        <Divider sx={{ my: 4 }} />

        {/* ── Compras realizadas ─────────────────────────────────────────
            El partner compra ÉL MISMO hasta su techo. Esta tabla es lo que
            necesita para cuadrar su factura: cada compra con su FECHA, los
            CRÉDITOS que entraron y el IMPORTE en USD que se le factura por
            ella. Las dos columnas van separadas y con sus unidades a
            propósito: son cosas distintas y mezclarlas era la confusión. */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <ShoppingCartIcon color="primary" />
                  <Typography variant="h6">
                    {t('partner.purchases.title', 'Tus compras de créditos')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {hasCap
                    ? t('partner.purchases.help', 'Compras a granel cuando quieras. Este mes has comprado {{purchased}} de {{cap}} créditos de tu límite mensual: te quedan {{headroom}}{{renews}}.', {
                        purchased: formatCredits(purchasedThisCycle),
                        cap: formatCredits(capCredits),
                        headroom: formatCredits(headroomCredits ?? 0),
                        renews: renewsLabel ? ' · ' + t('partner.purchases.renews', 'se renueva el {{date}}', { date: renewsLabel }) : ''
                      })
                    : t('partner.purchases.helpNoCap', 'No tienes límite de compra mensual: compra los créditos que necesites.')}
                </Typography>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<AddShoppingCartIcon />}
                  onClick={() => setPurchaseOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  {t('partner.purchase.title', 'Comprar créditos')}
                </Button>
                {/* Pedir más línea solo tiene sentido si hay techo. */}
                {hasCap && (
                  <Button
                    variant="outlined"
                    startIcon={<TrendingUpIcon />}
                    onClick={() => setRequestOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('partner.limitRequest.short', 'Ampliar límite mensual')}
                  </Button>
                )}
              </Box>
            </Box>
            {purchaseEntries.length === 0 ? (
              <Typography color="text.secondary" align="center" py={3}>
                {t('partner.purchases.empty', 'Todavía no has comprado créditos.')}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('partner.purchases.fields.date', 'Fecha')}</TableCell>
                      <TableCell>{t('partner.purchases.fields.concept', 'Concepto')}</TableCell>
                      <TableCell align="right">{t('partner.purchases.fields.credits', 'Créditos')}</TableCell>
                      <TableCell align="right">{t('partner.purchases.fields.amountUsd', 'Importe (USD)')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseEntries.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>{formatMoment(entry.created_at)}</TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>
                          <Typography variant="caption" color="text.secondary">
                            {entry.concept || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            +{formatCredits(entry.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {/* El importe lo calcula el BACKEND con la misma
                              aritmética del statement: aquí no se deriva nada,
                              para que compra y factura no puedan discrepar. */}
                          <Typography variant="body2" fontWeight={700}>
                            {entry.amount_usd === null || entry.amount_usd === undefined
                              ? '—'
                              : `${Number(entry.amount_usd).toFixed(2)} USD`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* ── Solicitudes de ampliación de techo ─────────────────────────
            Lo ÚNICO que el partner tiene que pedir: los créditos los compra
            solo. Aprobar esto no le carga saldo, le sube el límite. */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Box>
                <Typography variant="h6">
                  {t('partner.limitRequest.sectionTitle', 'Solicitudes de ampliación de límite mensual')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('partner.limitRequest.sectionHelp', 'Tu límite de compra mensual es cuánto puedes comprar cada mes. Si se te queda corto, pide ampliarlo: aprobarlo no te carga créditos, te sube el límite.')}
                </Typography>
              </Box>
            </Box>
            {requests.length === 0 ? (
              <Typography color="text.secondary" align="center" py={3}>
                {t('partner.limitRequest.empty', 'No has pedido ninguna ampliación.')}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('partner.limitRequest.fields.date', 'Fecha')}</TableCell>
                      <TableCell align="right">{t('partner.limitRequest.fields.current', 'Techo entonces')}</TableCell>
                      <TableCell align="right">{t('partner.limitRequest.fields.requested', 'Techo pedido')}</TableCell>
                      <TableCell align="right">{t('partner.limitRequest.fields.granted', 'Techo concedido')}</TableCell>
                      <TableCell>{t('partner.limitRequest.fields.status', 'Estado')}</TableCell>
                      <TableCell>{t('partner.limitRequest.fields.detail', 'Detalle')}</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {requests.map((req) => (
                      <TableRow key={req.id} hover>
                        <TableCell>{formatMoment(req.created_at)}</TableCell>
                        <TableCell align="right">
                          {req.current_max_credits === null || req.current_max_credits === undefined
                            ? '—'
                            : formatCredits(req.current_max_credits)}
                        </TableCell>
                        <TableCell align="right">{formatCredits(req.requested_max_credits)}</TableCell>
                        <TableCell align="right">
                          {/* «Concedido» solo si se aprobó: el admin puede
                              conceder menos de lo pedido y esa es la cifra
                              que de verdad rige. */}
                          {req.status === 'approved'
                            ? formatCredits(req.approved_max_credits ?? req.requested_max_credits)
                            : '—'}
                        </TableCell>
                        <TableCell><LimitRequestStatusChip status={req.status} /></TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="caption" color="text.secondary">
                            {/* El motivo del marketplace manda sobre la nota
                                propia: es la información nueva para el partner. */}
                            {req.decision_note || req.note || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {req.status === 'pending' && (
                            <Button
                              size="small"
                              color="inherit"
                              disabled={cancelling === req.id}
                              onClick={() => handleCancelRequest(req)}
                              sx={{ textTransform: 'none' }}
                            >
                              {t('partner.limitRequest.cancel', 'Retirar')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* ── Extracto completo de la bolsa ──────────────────────────────
            El statement de arriba es mensual y agregado: dice cuánto, no cuál.
            Esto es movimiento a movimiento —compras, asignaciones a clientes,
            ajustes y cargos de disco— y es lo que contesta a «¿por qué me bajó
            el saldo el martes?» sin tener que preguntar. Todo en CRÉDITOS: el
            importe en dinero solo existe en las compras y ya tiene su tabla. */}
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <SwapVertIcon color="primary" />
                <Typography variant="h6">
                  {t('partner.transactions.title', 'Movimientos de tu bolsa')}
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('partner.transactions.filter', 'Tipo de movimiento')}</InputLabel>
                <Select
                  value={entriesKind}
                  label={t('partner.transactions.filter', 'Tipo de movimiento')}
                  onChange={(e) => setEntriesKind(String(e.target.value))}
                >
                  {KIND_FILTERS.map((k) => (
                    <MenuItem key={k || 'all'} value={k}>
                      {k ? entryKindLabel(k) : t('partner.transactions.kind.all', 'Todos')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {entriesLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
              </Box>
            ) : entries.length === 0 ? (
              <Typography color="text.secondary" align="center" py={3}>
                {t('partner.transactions.empty', 'Todavía no hay movimientos en tu bolsa.')}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('partner.transactions.fields.date', 'Fecha')}</TableCell>
                      <TableCell>{t('partner.transactions.fields.kind', 'Tipo')}</TableCell>
                      <TableCell>{t('partner.transactions.fields.concept', 'Concepto')}</TableCell>
                      <TableCell align="right">{t('partner.transactions.fields.credits', 'Créditos')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry) => {
                      const amount = Number(entry.amount);
                      return (
                        <TableRow key={entry.id} hover>
                          <TableCell>{formatMoment(entry.created_at)}</TableCell>
                          <TableCell>
                            <Chip size="small" variant="outlined" label={entryKindLabel(entry.kind)} />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 380 }}>
                            <Typography variant="caption" color="text.secondary">
                              {entry.concept || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color={amount < 0 ? 'error.main' : 'success.main'}
                            >
                              {amount > 0 ? '+' : ''}{formatCredits(entry.amount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Comprar: self-service hasta el techo. Si no cabe, el propio diálogo
          ofrece pedir ampliación — es la única salida y no debe haber que
          buscarla. */}
      <PurchaseCreditsDialog
        open={purchaseOpen}
        onClose={() => setPurchaseOpen(false)}
        onPurchased={refreshAfterPurchase}
        maxWholesaleCredits={reseller?.max_wholesale_credits ?? null}
        purchasedThisCycle={reseller?.wholesale_purchased_this_cycle ?? null}
        cycleRenewsAt={reseller?.wholesale_cycle_renews_at ?? null}
        discountPct={reseller?.wholesale_discount_pct ?? null}
        creditPriceUsd={creditPrice}
        onRequestLimit={() => setRequestOpen(true)}
      />
      <LimitRequestDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        onCreated={refreshAfterRequest}
        currentMax={reseller?.max_wholesale_credits ?? null}
        wholesaleBalance={reseller?.wholesale_balance ?? null}
      />
    </PartnerLayout>
  );
};

export default PartnerBillingPage;
